/**
 * hub.js — the carnival midway (GDD 10.2).
 *
 * Builds the ground, the entrance gate, twelve stall plots in two rows of six,
 * the prize counter, the personal-space portal, leaderboard boards and the
 * string lights, then registers a teleport node at every one of them.
 *
 * Each stall plot exposes a `root` Group whose local +Z points INTO the booth
 * and whose origin is where the player stands. Stall modules author all their
 * geometry in that local space and never think about world coordinates or
 * which side of the midway they are on. This is the contract that lets twelve
 * stalls be written independently.
 *
 * @module world/hub
 */

import * as THREE from 'three';
import { HUB, STALLS, PALETTE, stallPlayOrigin } from '../constants.js';
import * as physics from '../core/physics.js';
import { teleport } from '../core/teleport.js';
import * as factory from '../assets3d/factory.js';
import { midwayDirt } from '../assets3d/textures.js';
import { createSky } from './sky.js';
import { createLighting } from './lighting.js';

/**
 * Rewrite a geometry's 0–1 UVs as metres, so one tiling ground texture keeps
 * the same world-space scale on meshes of very different sizes.
 * @param {THREE.BufferGeometry} geometry
 * @param {number} uMetres Extent along the geometry's U axis.
 * @param {number} vMetres Extent along its V axis.
 * @returns {THREE.BufferGeometry} The same geometry.
 */
function metreUVs(geometry, uMetres, vMetres) {
  const uv = geometry.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * uMetres, uv.getY(i) * vMetres);
  uv.needsUpdate = true;
  return geometry;
}

/**
 * @typedef {Object} StallPlot
 * @property {import('../constants.js').StallSpec} spec
 * @property {THREE.Group} root Origin at the play position, +Z into the booth.
 * @property {THREE.Group} booth
 * @property {THREE.Vector3} playPosition World-space spot the player stands at.
 * @property {number} yaw Facing applied on arrival.
 * @property {string} teleportNodeId
 * @property {THREE.Mesh} sign
 * @property {boolean} implemented Flipped true when a stall module registers.
 */

class Hub {
  constructor() {
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {THREE.Group} */ this.root = null;
    /** @type {Map<string, StallPlot>} */ this.plots = new Map();
    /** @type {Array<{update:(dt:number,elapsed:number)=>void}>} */ this._animated = [];
    this._elapsed = 0;
  }

  /**
   * @param {THREE.Scene} scene
   * @param {{ shadows?: boolean }} [opts]
   */
  build(scene, opts = {}) {
    this.scene = scene;
    this.root = new THREE.Group();
    this.root.name = 'hub';
    scene.add(this.root);

    createSky(scene);
    createLighting(scene, { shadows: opts.shadows !== false });

    this._buildGround();
    this._buildEntrance();
    this._buildStallPlots();
    this._buildEndCap();
    this._buildDressing();

    return this;
  }

  /**
   * @param {string} stallId
   * @returns {StallPlot|undefined}
   */
  getPlot(stallId) {
    return this.plots.get(stallId);
  }

  /** @returns {StallPlot[]} */
  allPlots() {
    return [...this.plots.values()];
  }

  /**
   * Replace a plot's COMING SOON sign with the real stall name.
   * Called by the stall framework when an implemented stall claims its plot.
   * @param {string} stallId
   */
  markImplemented(stallId) {
    const plot = this.plots.get(stallId);
    if (!plot) return;
    plot.implemented = true;
    const boothSign = plot.booth.getObjectByName('booth-sign');
    if (boothSign && boothSign.userData.setText) boothSign.userData.setText(plot.spec.name);
    plot.sign.userData.setText([plot.spec.name, `${plot.spec.cost} TICKETS`]);
  }

  update(dt) {
    this._elapsed += dt;
    for (const a of this._animated) a.update(dt, this._elapsed);
  }

  // ── Construction ──────────────────────────────────────────────────────────

  /** @private */
  _buildGround() {
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(HUB.groundSize, HUB.groundSize),
      factory.mat(PALETTE.ground, { rough: 1 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    ground.name = 'ground';
    this.root.add(ground);

    // One texture serves every trodden surface. The meshes carry UVs in metres
    // rather than 0–1, so it tiles at the same scale on the long thin strip and
    // on the plaza disc without needing a second copy at a second repeat.
    const earth = factory.mat(0xffffff, { rough: 0.98, map: midwayDirt(PALETTE.path) });

    // The walkway itself — a lighter strip running the length of the midway.
    // It must start well behind the spawn point, or its leading edge cuts
    // across the frame right at the player's feet.
    const pathStart = HUB.spawn.z + 12;
    const pathEnd = HUB.prizeCounter.z - 12;
    const pathLength = pathStart - pathEnd;
    const pathWidth = HUB.walkwayHalfWidth * 2;
    const path = new THREE.Mesh(
      metreUVs(new THREE.PlaneGeometry(pathWidth, pathLength), pathWidth, pathLength),
      earth,
    );
    path.rotation.x = -Math.PI / 2;
    path.position.set(0, 0.012, pathStart - pathLength / 2);
    path.receiveShadow = true;
    path.name = 'walkway';
    this.root.add(path);

    // A plaza at the far end. The prize desk, both portals and the ground
    // behind them all sit outside the 8 m strip, so the path used to stop dead
    // and dark ground showed through around the whole end cap. Radius and
    // centre are picked so the disc swallows the strip's far edge (z −50.5,
    // where it is still 5.8 m wide) as well as every end-cap landmark.
    const plazaRadius = 12;
    const plaza = new THREE.Mesh(
      metreUVs(new THREE.CircleGeometry(plazaRadius, 64), plazaRadius * 2, plazaRadius * 2),
      earth,
    );
    plaza.rotation.x = -Math.PI / 2;
    // Between the ground (0) and the walkway (0.012), with the same 6 mm clear
    // of each — enough at this scene's near/far to keep the three planes apart.
    plaza.position.set(0, 0.006, HUB.prizeCounter.z - 1.5);
    plaza.receiveShadow = true;
    plaza.name = 'end-plaza';
    this.root.add(plaza);

    // The hub needs a real floor collider, not just a visual plane — without
    // it anything dropped outside a stall falls through the world forever.
    physics.addBody(null, {
      type: 'fixed',
      shape: 'box',
      halfExtents: { x: HUB.groundSize / 2, y: 0.5, z: HUB.groundSize / 2 },
      position: { x: 0, y: -0.5, z: 0 },
      friction: 0.85,
      restitution: 0.12,
      tag: 'hub-ground',
    });
  }

  /** @private */
  _buildEntrance() {
    const gate = factory.createEntranceGate(HUB.walkwayHalfWidth * 2 + 2.5, 5.5);
    gate.position.set(0, 0, HUB.spawn.z + 2.5);
    this.root.add(gate);
    this._animated.push({
      update: (dt, elapsed) => gate.userData.lights?.userData.twinkle(elapsed),
    });

    teleport.addNode({
      id: 'hub-entrance',
      position: new THREE.Vector3(HUB.spawn.x, 0, HUB.spawn.z),
      yaw: HUB.spawnYaw,
      label: 'Entrance',
      group: 'landmark',
    });

    // Leaderboard boards flanking the gate (GDD 10.2).
    HUB.leaderboards.forEach((pos, i) => {
      const board = factory.createLeaderboardBoard(
        i === 0 ? 'WEEKLY LEADERS' : 'ALL-TIME LEADERS',
      );
      board.position.set(pos.x, 0, pos.z);
      board.rotation.y = pos.yaw;
      board.name = `leaderboard-${i === 0 ? 'weekly' : 'alltime'}`;
      this.root.add(board);
    });

    teleport.addNode({
      id: 'hub-leaderboards',
      position: new THREE.Vector3(0, 0, HUB.leaderboards[0].z - 1),
      yaw: HUB.spawnYaw,
      label: 'Leaderboards',
      group: 'landmark',
    });
  }

  /** @private */
  _buildStallPlots() {
    for (const spec of STALLS) {
      const origin = stallPlayOrigin(spec);

      const root = new THREE.Group();
      root.name = `plot-${spec.id}`;
      root.position.set(origin.x, 0, origin.z);
      root.rotation.y = origin.yaw;
      this.root.add(root);

      // The booth sits ahead of the play origin, opening back toward it.
      const booth = factory.createStallBooth({
        name: spec.name,
        color: spec.color,
        width: HUB.boothWidth,
        depth: HUB.boothDepth,
        height: HUB.boothHeight,
        comingSoon: true,
      });
      booth.position.set(0, 0, HUB.boothX - HUB.playOriginX);
      root.add(booth);

      // Ticket-price sign hanging above the counter, facing the player.
      const sign = factory.createSign('', {
        w: 2.0, h: 0.62,
        lines: [spec.name, `${spec.cost} TICKETS`],
        bg: '#12172a',
        size: 58,
      });
      // Hangs under the booth header, above the counter, facing the player.
      sign.position.set(0, 2.35, HUB.boothX - HUB.playOriginX + 0.12);
      sign.rotation.y = Math.PI;
      sign.name = `sign-${spec.id}`;
      root.add(sign);

      const node = teleport.addNode({
        id: `stall-${spec.id}`,
        position: new THREE.Vector3(origin.x, 0, origin.z),
        yaw: origin.yaw,
        label: spec.name,
        group: 'stall',
      });

      this.plots.set(spec.id, {
        spec,
        root,
        booth,
        sign,
        playPosition: new THREE.Vector3(origin.x, 0, origin.z),
        yaw: origin.yaw,
        teleportNodeId: node.id,
        implemented: false,
      });

      // A lamp string spanning each booth front.
      const lights = factory.createLampString(HUB.boothWidth, 8);
      lights.position.set(0, HUB.boothHeight + 0.1, HUB.boothX - HUB.playOriginX - 0.5);
      lights.rotation.y = Math.PI / 2;
      root.add(lights);
      this._animated.push({ update: (dt, e) => lights.userData.twinkle(e + spec.slot) });
    }
  }

  /** @private */
  _buildEndCap() {
    const desk = factory.createPrizeDesk();
    desk.position.set(HUB.prizeCounter.x, 0, HUB.prizeCounter.z);
    desk.rotation.y = 0;
    desk.name = 'prize-counter';
    this.root.add(desk);

    teleport.addNode({
      id: 'prize-counter',
      position: new THREE.Vector3(HUB.prizeCounter.x, 0, HUB.prizeCounter.z + 2.2),
      yaw: 0,
      label: 'Prize Desk',
      group: 'landmark',
    });

    const portal = factory.createPortal(0x9b5de5, 1.1);
    portal.position.set(HUB.personalSpacePortal.x, 0, HUB.personalSpacePortal.z);
    portal.name = 'personal-space-portal';
    this.root.add(portal);
    this._animated.push({ update: (dt, e) => portal.userData.pulse(e) });

    // A sign's readable face is its local +Z, and the player walks toward −Z,
    // so no rotation — turned 180° it showed its opaque plum backing board.
    // z + 0.16 clears the ring: the portal's torus reaches z + 0.12.
    const portalSign = factory.createSign('YOUR SPACE', { w: 1.8, h: 0.5 });
    portalSign.position.set(HUB.personalSpacePortal.x, 2.7, HUB.personalSpacePortal.z + 0.16);
    this.root.add(portalSign);

    teleport.addNode({
      id: 'personal-space',
      position: new THREE.Vector3(HUB.personalSpacePortal.x, 0, HUB.personalSpacePortal.z + 2.2),
      yaw: 0,
      label: 'Your Space',
      group: 'landmark',
    });

    // Midway waypoints so teleport hops between distant stalls stay short.
    for (let i = 0; i < 6; i++) {
      teleport.addNode({
        id: `midway-${i}`,
        position: new THREE.Vector3(0, 0, HUB.firstSlotZ - i * HUB.slotSpacing - HUB.slotSpacing / 2),
        yaw: HUB.spawnYaw,
        label: `Midway ${i + 1}`,
        group: 'hub',
      });
    }
  }

  /** @private */
  _buildDressing() {
    const wheel = factory.createFerrisWheel(9);
    wheel.position.set(-28, 0, -26);
    this.root.add(wheel);
    this._animated.push({ update: (dt) => wheel.userData.spin(dt) });

    // Trees along the far edges, biased away from the walkway.
    const spots = [
      [-20, 12], [-24, -2], [-19, -18], [-26, -34], [-16, -44],
      [21, 10], [26, -6], [19, -22], [27, -32], [17, -45],
      [-11, 16], [12, 17], [-30, -14], [30, -18],
    ];
    for (let i = 0; i < spots.length; i++) {
      const [x, z] = spots[i];
      const tree = factory.createTree(3.4 + (i % 4) * 0.7);
      tree.position.set(x, 0, z);
      tree.rotation.y = i * 1.3;
      this.root.add(tree);
    }

    // String lights across the walkway between booth pairs.
    for (let i = 0; i < 6; i++) {
      const span = factory.createLampString(HUB.playOriginX * 2 + 2, 12);
      span.position.set(0, 4.4, HUB.firstSlotZ - i * HUB.slotSpacing - HUB.slotSpacing / 2);
      this.root.add(span);
      this._animated.push({ update: (dt, e) => span.userData.twinkle(e + i * 0.7) });
    }
  }
}

/** The one hub. */
export const hub = new Hub();
